import { FROM_TEAM_MEMBER, aiNameOf, prettyApp, prettyModel, type TicketHistoryLine } from "@omnabase/shared";
import { threadWithEvents, type IconName, type ThreadEvent } from "@omnabase/ui";

/** The ticket's history as the thread's event lines (the ticket page and marks round, 2026-10-23; before it the kit's History block under
 *  the thread, M9 round three). Every ticket shows them: one thin line per change, at its time among the messages (`threadWithEvents`, the
 *  kit's merge, re-exported here). Left out: the lines that only repeat what the thread already shows, the first message ("Ticket started"),
 *  its band ("From a team member"), a reply or a note (the message itself). Each line names who acted: a member, a member's AI as "<name>'s
 *  AI" with the app and the model it reported on hover, or a connected site by its host (M12 round three). An assignment names its assignee
 *  ("Assigned to Kriesi by Kriesi's AI", "Work my queue" round two). Pure. */
const REPEATS: readonly string[] = ["Ticket started", FROM_TEAM_MEMBER, "Reply sent", "Note added"];
type NameOf = (userId: string | null) => string;
export function historyLines(lines: TicketHistoryLine[], nameOf: NameOf): ThreadEvent[] {
  return lines.flatMap((l, i) => (REPEATS.includes(l.what) ? [] : [eventOf(l, i, nameOf)]));
}
function eventOf(l: TicketHistoryLine, i: number, nameOf: NameOf): ThreadEvent {
  const title = l.via === "connection" ? appAndModel(l) : null;
  return { id: `${l.at}-${i}`, what: wordsOf(l, nameOf), by: byOf(l, nameOf), at: l.at, icon: iconOf(l.what), ...(title ? { title } : {}) };
}
export { threadWithEvents };
/** who acted: a member, a member's AI ("Kriesi's AI"), or a connected site by its host ("Set to pending by forum.kriesi.at") */
const byOf = (l: TicketHistoryLine, nameOf: NameOf) => (l.via === "connection" ? aiNameOf(nameOf(l.actorId)) : l.via === "api_key" ? l.site ?? "a connected site" : nameOf(l.actorId));
/** the hover of a member's AI's line: "Claude Code · Opus 5.5, as reported by the AI"; null when neither was reported */
function appAndModel(l: TicketHistoryLine): string | null {
  const app = prettyApp(l.app), model = prettyModel(l.model);
  if (!app && !model) return null;
  return [app, model && `${model}, as reported by the AI`].filter(Boolean).join(" · ");
}
/** the line's words, an assignment with its assignee's name */
const wordsOf = (l: TicketHistoryLine, nameOf: NameOf) => (l.what === "Assigned" && l.assigneeId ? `Assigned to ${nameOf(l.assigneeId)}` : l.what);
/** a small icon by the kind of change; the history sign for the rest */
function iconOf(what: string): IconName {
  if (/^Assigned|^Taken|^Given back/.test(what)) return "user";
  if (/^Snoozed/.test(what)) return "clock";
  if (/^Tags/.test(what)) return "tag";
  if (/spam/i.test(what)) return "spam";
  if (/^Finished|^Resolved/.test(what)) return "checkCircle";
  return "history";
}
